import { useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import {
  buildUpdateBody,
  editLinesFromOrder,
} from '../lib/purchase-order-lines'
import {
  LINES_CONTAINER,
  PurchaseOrderLineRow,
  PurchaseOrderLinesHeader,
} from './purchase-order-line-row'
import { NotOrderableList } from './not-orderable-list'
import type { DraftHeader, EditLine } from '../lib/purchase-order-lines'
import type {
  PurchaseOrder,
  PurchaseOrderUpdateBody,
} from '@/api/endpoints/purchase-orders'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ProductSkuCombobox } from '@/features/products/components/product-sku-combobox'

/**
 * A DRAFT as a form (design UI, task 6.4). The parent remounts it with every saved version
 * (`key`), so the local state always starts from what the server holds.
 *
 * Adding an article saves the draft at once: the server resolves its package levels and GTIN, or
 * refuses it with the reason it cannot be ordered — the manager sees both immediately.
 */
export function PurchaseOrderDraftEditor({
  order,
  canSend,
  busy,
  onSave,
  onSend,
  onDelete,
  onError,
}: {
  order: PurchaseOrder
  /** The Standort is configured; sending is possible. */
  canSend: boolean
  busy: boolean
  onSave: (body: PurchaseOrderUpdateBody) => Promise<unknown>
  /** `body` is set when there are unsaved changes, to be saved before sending. */
  onSend: (body: PurchaseOrderUpdateBody | null) => void
  onDelete: () => void
  onError: (message: string) => void
}) {
  const { t } = useTranslation('purchase-orders')
  const [header, setHeader] = useState<DraftHeader>({
    deliveryDate: order.deliveryDate,
    countsForPreorders: order.countsForPreorders,
    note: order.note ?? '',
  })
  const [lines, setLines] = useState<Array<EditLine>>(() =>
    editLinesFromOrder(order),
  )
  const [dirty, setDirty] = useState(false)

  const changeHeader = (next: Partial<DraftHeader>) => {
    setHeader((h) => ({ ...h, ...next }))
    setDirty(true)
  }
  const changeLines = (next: (prev: Array<EditLine>) => Array<EditLine>) => {
    setLines(next)
    setDirty(true)
  }

  const body = (extra: Array<{ sku: string }> = []) => {
    const result = buildUpdateBody(header, lines, extra)
    if (!result.ok) {
      onError(t(`validation.${result.error}`, { sku: result.sku ?? '' }))
      return null
    }
    return result.body
  }

  const addArticle = (sku: string, name: string) => {
    if (lines.some((l) => l.sku === sku)) {
      onError(t('detail.duplicate', { name }))
      return
    }
    const next = body([{ sku }])
    if (next) void onSave(next).catch(() => undefined)
  }

  const save = () => {
    const next = body()
    if (next) void onSave(next).catch(() => undefined)
  }

  const send = () => {
    if (!dirty) {
      onSend(null)
      return
    }
    const next = body()
    if (next) onSend(next)
  }

  return (
    <div className="flex flex-col gap-4 pb-24">
      <Card>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-2">
            <Label>{t('detail.supplier')}</Label>
            <p className="flex h-9 items-center text-sm font-medium">
              {order.supplierName ?? '–'}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="po-delivery-date">{t('detail.deliveryDate')}</Label>
            <Input
              id="po-delivery-date"
              type="date"
              value={header.deliveryDate}
              onChange={(e) => changeHeader({ deliveryDate: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="po-counts">{t('detail.countsForPreorders')}</Label>
            <div className="flex h-9 items-center">
              <Switch
                id="po-counts"
                checked={header.countsForPreorders}
                onCheckedChange={(v) => changeHeader({ countsForPreorders: v })}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="po-note">{t('detail.note')}</Label>
            <Input
              id="po-note"
              value={header.note}
              onChange={(e) => changeHeader({ note: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden pb-0">
        <CardHeader>
          <CardTitle>{t('detail.lines')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className={LINES_CONTAINER}>
            {lines.length > 0 && <PurchaseOrderLinesHeader readOnly={false} />}
            {lines.map((line) => (
              <PurchaseOrderLineRow
                key={line.key}
                line={line}
                onChange={(next) =>
                  changeLines((prev) =>
                    prev.map((l) => (l.key === line.key ? next : l)),
                  )
                }
                onRemove={() =>
                  changeLines((prev) => prev.filter((l) => l.key !== line.key))
                }
              />
            ))}
          </div>
          {lines.length === 0 && (
            <p className="border-t px-3 py-6 text-center text-sm text-muted-foreground">
              {t('detail.noLines')}
            </p>
          )}
          <div className="border-t p-3 sm:max-w-md">
            <ProductSkuCombobox
              value={null}
              onChange={(product) => {
                if (product) addArticle(product.sku, product.name)
              }}
              placeholder={t('detail.addArticle')}
              restaurantId={order.restaurantId}
              disabled={busy}
            />
          </div>
        </CardContent>
      </Card>

      <NotOrderableList items={order.notOrderable} />

      <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center justify-end gap-3 border-t bg-background px-4 py-3 sm:mx-0 sm:rounded-md sm:border">
        <Button
          type="button"
          variant="ghost"
          className="text-destructive sm:mr-auto"
          onClick={onDelete}
          disabled={busy}
        >
          {t('detail.delete')}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={save}
          disabled={busy || !dirty}
        >
          {t('detail.save')}
        </Button>
        <Button
          type="button"
          onClick={send}
          disabled={busy || !canSend || lines.length === 0}
        >
          {t('detail.send')}
        </Button>
      </div>
    </div>
  )
}
